<template>
  <div class="preset-bar">
    <div class="preset-chips">
      <draggable
        v-if="localList.length"
        v-model="localList"
        item-key="id"
        handle=".drag-grip"
        :animation="120"
        class="chip-row"
      >
        <template #item="{ element }">
          <div class="preset-chip" :class="{ active: element.id === activeId, dirty: element.id === dirtyId, editing: editingId === element.id }">
            <template v-if="editingId === element.id">
              <template v-if="removeConfirm">
                <span class="confirm-text">Удалить «{{ element.name }}»?</span>
                <button type="button" class="chip-act danger-text" title="Подтвердить" @click="confirmDelete(element.id)">Да</button>
                <button type="button" class="chip-act" title="Отмена" @click="cancelDelete">Нет</button>
              </template>
              <template v-else>
                <input
                  ref="editInput"
                  v-model="editText"
                  class="rename-input"
                  type="text"
                  maxlength="60"
                  @keydown.enter.prevent="commitEdit"
                  @keydown.esc.prevent="cancelDelete"
                  @blur="commitEdit"
                />
                <button type="button" class="chip-act ok" title="Сохранить имя" @mousedown.prevent="commitEdit">&#10003;</button>
                <button type="button" class="chip-act remove" title="Удалить пресет" @mousedown.prevent="askDelete">&#128465;</button>
                <button type="button" class="chip-act" title="Отмена" @mousedown.prevent="cancelEdit">&#10005;</button>
              </template>
            </template>
            <template v-else>
              <span class="drag-grip" aria-hidden="true" title="Перетащить" />
              <button
                v-if="defaultPresetEnabled"
                type="button"
                class="star"
                :class="{ on: element.isDefault }"
                :title="element.isDefault ? 'Снять отметку «по умолчанию»' : 'Применять по умолчанию'"
                @click="$emit('setDefault', element.isDefault ? null : element.id)"
              >&#9733;</button>
              <button type="button" class="preset-name" :title="element.id === dirtyId ? 'Вид изменён — нажмите, чтобы вернуть пресет' : 'Применить пресет'" @click="$emit('apply', element.id)">
                <span v-if="element.id === dirtyId" class="dirty-dot" title="Текущий вид отличается от пресета" />
                <span class="name-label">{{ element.name }}</span>
              </button>
              <button type="button" class="chip-act pencil" title="Изменить / удалить" @click="startEdit(element.id)">&#9998;</button>
            </template>
          </div>
        </template>
      </draggable>
      <span v-else class="preset-hint">Настройте фильтры и сохраните пресет для быстрого доступа</span>
    </div>

    <div class="preset-save">
      <template v-if="saving">
        <input
          ref="saveInput"
          v-model="saveName"
          class="save-input"
          type="text"
          maxlength="60"
          placeholder="Название пресета"
          @keydown.enter.prevent="commitSave"
          @keydown.esc.prevent="cancelSave"
        />
        <button type="button" class="preset-btn primary" @click="commitSave">Сохранить</button>
        <button type="button" class="preset-btn" @click="cancelSave">Отмена</button>
      </template>
      <button v-else type="button" class="preset-btn" @click="startSave">+ Сохранить фильтры</button>
    </div>
  </div>
</template>

<script>
import { nextTick, ref, watch } from 'vue';
import draggable from 'vuedraggable';

export default {
  name: 'FilterPresetBar',
  components: { draggable },
  props: {
    presets: { type: Array, default: () => [] },
    activeId: { type: String, default: null },
    dirtyId: { type: String, default: null },
  },
  emits: ['apply', 'save', 'rename', 'remove', 'reorder', 'setDefault'],
  setup(props, { emit }) {
    const defaultPresetEnabled = false;
    const localList = ref(props.presets.slice());
    const saving = ref(false);
    const saveName = ref('');
    const saveInput = ref(null);
    const editingId = ref(null);
    const editText = ref('');
    const editInput = ref(null);
    const removeConfirm = ref(false);

    watch(() => props.presets, (val) => {
      localList.value = val.slice();
      if (editingId.value && !val.some((p) => p.id === editingId.value)) {
        editingId.value = null;
        removeConfirm.value = false;
      }
    }, { deep: false });
    watch(localList, (val) => {
      const newIds = val.map((p) => p.id);
      const curIds = props.presets.map((p) => p.id);
      const changed = newIds.length !== curIds.length || newIds.some((id, i) => id !== curIds[i]);
      if (changed) emit('reorder', newIds);
    }, { deep: true });

    const startSave = async () => {
      saving.value = true;
      saveName.value = '';
      await nextTick();
      saveInput.value?.focus();
    };
    const commitSave = () => {
      const name = saveName.value.trim();
      if (!name) return;
      emit('save', name);
      saving.value = false;
      saveName.value = '';
    };
    const cancelSave = () => { saving.value = false; saveName.value = ''; };

    const startEdit = async (id) => {
      const target = props.presets.find((p) => p.id === id);
      editingId.value = id;
      editText.value = target ? target.name : '';
      removeConfirm.value = false;
      await nextTick();
      editInput.value?.focus();
      editInput.value?.select();
    };
    const commitEdit = () => {
      const id = editingId.value;
      if (!id) return;
      const name = editText.value.trim();
      if (name) emit('rename', { id, name });
      editingId.value = null;
      removeConfirm.value = false;
    };
    const cancelEdit = () => { editingId.value = null; removeConfirm.value = false; };
    const askDelete = () => { removeConfirm.value = true; };
    const cancelDelete = () => { removeConfirm.value = false; };
    const confirmDelete = (id) => {
      editingId.value = null;
      removeConfirm.value = false;
      emit('remove', id);
    };

    return {
      defaultPresetEnabled,
      localList, saving, saveName, saveInput, editingId, editText, editInput, removeConfirm,
      startSave, commitSave, cancelSave, startEdit, commitEdit, cancelEdit,
      askDelete, cancelDelete, confirmDelete,
    };
  },
};
</script>

<style scoped>
.preset-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #fbfcfd;
}
.preset-chips { flex: 1; min-width: 0; display: flex; align-items: center; }
.chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.preset-hint { color: #98a2b3; font-size: 13px; }

.preset-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px 2px 6px;
  border: 1px solid #ccc;
  border-radius: 14px;
  background: #fff;
  color: #344054;
  font-size: 13px;
  max-width: 100%;
}
.preset-chip.active { border-color: #007bff; background: #007bff; color: #fff; }
.preset-chip.dirty { border-style: dashed; border-color: #f59e0b; background: #fffbeb; color: #92400e; }
.preset-chip.dirty.active { border-color: #f59e0b; background: #fde68a; color: #92400e; }
.preset-chip.editing { border-radius: 10px; padding: 3px 5px; }

.drag-grip {
  width: 12px;
  height: 18px;
  cursor: grab;
  flex-shrink: 0;
  background-image: radial-gradient(circle, currentColor 1.3px, transparent 1.5px);
  background-size: 6px 6px;
  opacity: .5;
}
.preset-chip:hover .drag-grip { opacity: .9; }

.star {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
}
.star.on { color: #f5a623; }
.preset-chip.active .star { color: rgba(255, 255, 255, .6); }
.preset-chip.active .star.on { color: #ffd54a; }

.preset-name {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 240px;
  padding: 3px 2px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}
.name-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.dirty-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

.rename-input {
  width: 150px;
  padding: 1px 5px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font: inherit;
}

.chip-act {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  opacity: .65;
  flex-shrink: 0;
}
.chip-act:hover { opacity: 1; background: rgba(0, 0, 0, .08); }
.preset-chip.active .chip-act:hover { background: rgba(255, 255, 255, .25); }
.chip-act.pencil { opacity: .55; }
.chip-act.ok { color: #16a34a; opacity: .85; }
.chip-act.ok:hover { background: #dcfce7; }
.chip-act.remove { color: #dc2626; opacity: .7; }
.chip-act.remove:hover { background: #fee2e2; }
.chip-act.danger-text { width: auto; padding: 0 6px; border-radius: 10px; opacity: 1; color: #b91c1c; font-weight: 600; }
.confirm-text { padding: 0 4px; white-space: nowrap; }

.preset-save { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.save-input {
  width: 180px;
  height: 28px;
  padding: 3px 8px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 13px;
  box-sizing: border-box;
}
.preset-btn {
  height: 28px;
  padding: 2px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background: #fff;
  color: #007bff;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}
.preset-btn:hover { background: #007bff; color: #fff; }
.preset-btn.primary { background: #007bff; color: #fff; }
.preset-btn.primary:hover { background: #0062cc; }

@media (max-width: 700px) {
  .preset-bar { gap: 6px; }
  .preset-save { width: 100%; }
  .save-input { flex: 1; width: auto; }
}
</style>
